import { Bell, Music, Loader2, MessageSquare, Share2, ShoppingBag, ShieldCheck, Megaphone, Moon, Vibrate } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SettingsRow from '../../components/settings/SettingsRow';
import Toggle from '../../components/settings/Toggle';
import AutomationPanel from '../../components/settings/AutomationPanel';
import { useSettingsStore } from '../../store/settingsStore';
import { settingsService } from '../../services/settings/settings.service';
import type { NotificationPreferences, NotificationChannel } from '../../types/settings.types';
import { cn } from '../../utils/helpers';

type CatKey = keyof NotificationPreferences['categories'];

const CATEGORIES: { key: CatKey; label: string; description: string; icon: LucideIcon }[] = [
  { key: 'messages', label: 'Messages', description: 'Direct messages and replies', icon: MessageSquare },
  { key: 'social', label: 'Social', description: 'Likes, follows and mentions', icon: Share2 },
  { key: 'commerce', label: 'Commerce', description: 'Orders, shipping and receipts', icon: ShoppingBag },
  { key: 'security', label: 'Security', description: 'Sign-ins and account changes', icon: ShieldCheck },
  { key: 'marketing', label: 'Marketing', description: 'News, tips and promotions', icon: Megaphone },
];

const CHANNELS: { key: keyof NotificationChannel; label: string }[] = [
  { key: 'push', label: 'Push' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

export default function Notifications() {
  const { data, loading, saveNotifications } = useSettingsStore();

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const n = data.notifications;
  const soundName = settingsService.getNotificationSounds().find((s) => s.id === n.sound)?.name ?? n.sound;

  const setChannel = (cat: CatKey, channel: keyof NotificationChannel, value: boolean) => {
    saveNotifications({ categories: { ...n.categories, [cat]: { ...n.categories[cat], [channel]: value } } });
  };

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Choose what you hear about and how</p>
      </div>

      <SettingCard>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-neutral-900 text-white flex items-center justify-center"><Bell className="w-5 h-5" /></div>
            <div>
              <p className="font-bold">All notifications</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Master switch for every channel below</p>
            </div>
          </div>
          <Toggle checked={n.master} onChange={(v) => saveNotifications({ master: v })} label="All notifications" />
        </div>
      </SettingCard>

      <SettingCard title="By category" description="Pick a channel for each type of alert" flush>
        <div className={cn('transition-opacity', !n.master && 'opacity-40 pointer-events-none')}>
          {CATEGORIES.map((cat) => (
            <div key={cat.key} className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
              <div className="flex items-center gap-3.5 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><cat.icon className="w-5 h-5" /></div>
                <div>
                  <p className="font-semibold text-sm">{cat.label}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{cat.description}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pl-[3.375rem]">
                {CHANNELS.map((ch) => {
                  const active = n.categories[cat.key][ch.key];
                  return (
                    <button
                      key={ch.key}
                      onClick={() => setChannel(cat.key, ch.key, !active)}
                      className={cn(
                        'px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all',
                        active ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-700',
                      )}
                    >
                      {ch.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SettingCard>

      <SettingCard title="Sound & delivery" flush>
        <SettingsRow icon={Music} label="Notification sound" description="Preview and pick your alert tone" to="/app/settings/notifications/sounds" trailing={<span className="text-sm text-neutral-500 dark:text-neutral-400">{soundName}</span>} />
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Vibrate className="w-5 h-5" /></div>
            <div><p className="font-semibold text-sm">Vibration</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Vibrate on new alerts</p></div>
          </div>
          <Toggle checked={n.vibration} onChange={(v) => saveNotifications({ vibration: v })} label="Vibration" />
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Moon className="w-5 h-5" /></div>
            <div><p className="font-semibold text-sm">Quiet hours</p><p className="text-xs text-neutral-500 dark:text-neutral-400">{n.quietHoursEnabled ? `Muted ${n.quietHoursStart}–${n.quietHoursEnd}` : 'Silence alerts overnight'}</p></div>
          </div>
          <div className="flex items-center gap-2">
            {n.quietHoursEnabled && (
              <>
                <input type="time" value={n.quietHoursStart} onChange={(e) => saveNotifications({ quietHoursStart: e.target.value })} className="rounded-lg border border-neutral-200 dark:border-neutral-800 px-2 py-1 text-xs" />
                <input type="time" value={n.quietHoursEnd} onChange={(e) => saveNotifications({ quietHoursEnd: e.target.value })} className="rounded-lg border border-neutral-200 dark:border-neutral-800 px-2 py-1 text-xs" />
              </>
            )}
            <Toggle checked={n.quietHoursEnabled} onChange={(v) => saveNotifications({ quietHoursEnabled: v })} label="Quiet hours" />
          </div>
        </div>
      </SettingCard>

      <AutomationPanel category="notifications" title="Notification automation" description="AI keeps alerts relevant and well-timed" />
    </div>
  );
}
